import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from './services/api';

interface Zone {
  id: number;
  row_number: number;
  zone_number: number;
  label: string;
  weekday_price: number;
  weekend_price: number;
  weekday_extra_person_fee: number;
  weekend_extra_person_fee: number;
  min_persons: number;
  max_persons: number;
  active: boolean;
}

interface SelectedZone {
  zone_id: number;
  person_count: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ManualSunbedBookingModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const [zones, setZones] = useState<Zone[]>([]);
  const [occupiedZoneIds, setOccupiedZoneIds] = useState<Set<number>>(new Set());
  const [weekendDays, setWeekendDays] = useState<number[]>([0, 6]);

  const [bookingDate, setBookingDate] = useState<string>('');
  const [selected, setSelected] = useState<SelectedZone[]>([]);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'pending'>('paid');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Tarih default = bugün
  useEffect(() => {
    if (isOpen && !bookingDate) {
      const today = new Date().toISOString().split('T')[0];
      setBookingDate(today);
    }
  }, [isOpen, bookingDate]);

  // Zone listesi (admin endpoint -> public endpoint fallback)
  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/products/sunbed/zones')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setZones(d.data.zones || []);
          if (d.data.general?.weekend_days) setWeekendDays(d.data.general.weekend_days);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  // Müsaitlik
  useEffect(() => {
    if (!isOpen || !bookingDate) {
      setOccupiedZoneIds(new Set());
      return;
    }
    fetch(`/api/products/sunbed/zones/availability/${bookingDate}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setOccupiedZoneIds(new Set((d.data.occupied || []).map((o: any) => o.zone_id)));
        }
      })
      .catch(() => {});
    setSelected([]);
  }, [isOpen, bookingDate]);

  const isWeekend = useMemo(() => {
    if (!bookingDate) return false;
    return weekendDays.includes(new Date(bookingDate).getUTCDay());
  }, [bookingDate, weekendDays]);

  const zonePrice = (z: Zone, persons: number) => {
    const base = isWeekend ? z.weekend_price : z.weekday_price;
    const extraFee = isWeekend ? z.weekend_extra_person_fee : z.weekday_extra_person_fee;
    const extra = Math.max(0, persons - (z.min_persons || 2));
    return Number(base) + extra * Number(extraFee || 0);
  };

  const toggleZone = (zone: Zone) => {
    if (occupiedZoneIds.has(zone.id)) return;
    const exists = selected.find(s => s.zone_id === zone.id);
    if (exists) {
      setSelected(selected.filter(s => s.zone_id !== zone.id));
    } else {
      setSelected([...selected, { zone_id: zone.id, person_count: zone.min_persons || 2 }]);
    }
  };

  const updatePersons = (zoneId: number, persons: number) => {
    setSelected(selected.map(s => s.zone_id === zoneId ? { ...s, person_count: persons } : s));
  };

  const totalPrice = useMemo(() => {
    return selected.reduce((sum, s) => {
      const z = zones.find(zz => zz.id === s.zone_id);
      return sum + (z ? zonePrice(z, s.person_count) : 0);
    }, 0);
  }, [selected, zones, isWeekend]); // eslint-disable-line

  const totalPersons = selected.reduce((s, x) => s + x.person_count, 0);

  // Sıralara göre grupla
  const zonesByRow = useMemo(() => {
    const map: Record<number, Zone[]> = {};
    zones.forEach(z => {
      if (!map[z.row_number]) map[z.row_number] = [];
      map[z.row_number].push(z);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => a.zone_number - b.zone_number));
    return map;
  }, [zones]);

  const reset = () => {
    setSelected([]);
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
    setNotes('');
    setPaymentStatus('paid');
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    if (!bookingDate) { setError('Tarih seçin'); return; }
    if (selected.length === 0) { setError('En az bir bölge seçin'); return; }
    if (!customerName.trim()) { setError('Müşteri adı zorunlu'); return; }
    if (!customerPhone.trim()) { setError('Telefon zorunlu'); return; }

    setLoading(true);
    try {
      const res = await adminAPI.post('/admin/bookings/manual-sunbed', {
        booking_date: bookingDate,
        zones: selected,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail || undefined,
        notes: notes || undefined,
        payment_status: paymentStatus
      });
      if (res.data.success) {
        alert(`✅ Rezervasyon oluşturuldu\nReservation: ${res.data.data.reservation_id}\nToplam: ₺${res.data.data.total_price}`);
        reset();
        onSuccess?.();
        onClose();
      } else {
        setError(res.data.message || 'Hata');
      }
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Sunucu hatası');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const rowNumbers = Object.keys(zonesByRow).map(Number).sort((a, b) => a - b);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: '1rem'
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 10, maxWidth: 900, width: '100%',
        maxHeight: '90vh', overflow: 'auto', padding: '1.5rem'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>🏖️ Manuel Şezlong Rezervasyonu (Kapıdan)</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Tarih */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>📅 Tarih</label>
          <input
            type="date"
            value={bookingDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={e => setBookingDate(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.95rem' }}
          />
          {isWeekend && <span style={{ marginLeft: '0.75rem', color: '#e67e22', fontSize: '0.85rem' }}>⚡ Hafta sonu fiyatı</span>}
        </div>

        {/* Zone seçimi */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>🏖️ Bölge Seçimi</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {rowNumbers.map(rowNum => (
              <div key={rowNum}>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: 4 }}>{rowNum}. Sıra</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {zonesByRow[rowNum].map(z => {
                    const occupied = occupiedZoneIds.has(z.id);
                    const sel = selected.find(s => s.zone_id === z.id);
                    const price = zonePrice(z, sel?.person_count || z.min_persons);
                    return (
                      <button
                        key={z.id}
                        disabled={occupied || !z.active}
                        onClick={() => toggleZone(z)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: sel ? '2px solid #27ae60' : '1px solid #ccc',
                          borderRadius: 6,
                          background: occupied ? '#f5b7b1' : (sel ? '#d5f5e3' : '#fff'),
                          color: occupied ? '#922' : '#333',
                          cursor: occupied || !z.active ? 'not-allowed' : 'pointer',
                          fontSize: '0.8rem',
                          minWidth: 90
                        }}
                        title={occupied ? 'Dolu' : ''}
                      >
                        <div style={{ fontWeight: 600 }}>{z.label}</div>
                        <div style={{ fontSize: '0.75rem' }}>₺{price}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Seçili bölge listesi */}
        {selected.length > 0 && (
          <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Seçilen Bölgeler ({selected.length})</div>
            {selected.map(s => {
              const z = zones.find(zz => zz.id === s.zone_id);
              if (!z) return null;
              return (
                <div key={s.zone_id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: 4, fontSize: '0.9rem' }}>
                  <span style={{ minWidth: 140 }}>{z.label}</span>
                  <label style={{ fontSize: '0.85rem' }}>Kişi:</label>
                  <select
                    value={s.person_count}
                    onChange={e => updatePersons(s.zone_id, Number(e.target.value))}
                    style={{ padding: '0.25rem', borderRadius: 4 }}
                  >
                    {Array.from({ length: (z.max_persons - z.min_persons + 1) }, (_, i) => z.min_persons + i).map(n => (
                      <option key={n} value={n}>{n} kişi</option>
                    ))}
                  </select>
                  <span style={{ marginLeft: 'auto', fontWeight: 600 }}>₺{zonePrice(z, s.person_count)}</span>
                </div>
              );
            })}
            <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>Toplam ({totalPersons} kişi)</span>
              <span>₺{totalPrice}</span>
            </div>
          </div>
        )}

        {/* Müşteri bilgileri */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Müşteri Adı *</label>
            <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Telefon *</label>
            <input type="tel" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="05XXXXXXXXX"
              style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>E-posta (opsiyonel)</label>
            <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Ödeme Durumu</label>
            <select value={paymentStatus} onChange={e => setPaymentStatus(e.target.value as any)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }}>
              <option value="paid">💰 Peşin Alındı</option>
              <option value="pending">⏳ Sonra Ödenecek</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Notlar</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }} />
        </div>

        {error && (
          <div style={{ padding: '0.5rem', background: '#fadbd8', color: '#922', borderRadius: 6, marginBottom: '0.75rem' }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={loading}
            style={{ padding: '0.6rem 1rem', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>
            İptal
          </button>
          <button onClick={handleSubmit} disabled={loading || selected.length === 0}
            style={{ padding: '0.6rem 1.2rem', borderRadius: 6, border: 'none', background: '#27ae60', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {loading ? 'Kaydediliyor...' : `✅ Rezervasyonu Oluştur (₺${totalPrice})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualSunbedBookingModal;
